<template>
  <v-card
    class="client-card elevation-0"
    :class="{ 'has-issues': hasIssues }"
    height="100%"
  >
    <!-- Card Header -->
    <v-card-title class="subtitle-1 font-weight-bold pt-0">
      <v-col>
        <!-- Client Name and Status -->
        <v-row>
          <v-col
            class="pb-0 col-grow text-truncate clickable"
            :title="client.clientName"
            @click="$emit('view-profile', client)"
          >
            <span class="darkblue--text">
              {{ client.clientName || 'Name Unknown' }}
            </span>
          </v-col>

          <v-col class="col-auto pb-0 px-0 text-right">
            <!-- Status Chip -->
            <v-chip
              :color="clientStatusColor"
              :class="`ml-2 white--text`"
              small
            >
              {{ clientStatusText }}
            </v-chip>
          </v-col>

          <v-col class="col-auto pb-0 pt-2 px-0">
            <!-- Actions Menu -->
            <v-menu
              offset-y
              bottom
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  icon
                  v-on="on"
                >
                  <font-awesome-icon
                    :icon="['fas', 'ellipsis-vertical']"
                    size="lg"
                  />
                </v-btn>
              </template>

              <v-list dense>
                <v-list-item @click="$emit('view-profile', client)">
                  <v-list-item-title>View Profile</v-list-item-title>
                </v-list-item>

                <template v-if="isAdmin">
                  <v-divider />
                  <v-list-item @click="$emit('edit', client)">
                    <v-list-item-title>Edit</v-list-item-title>
                  </v-list-item>

                  <v-list-item @click="$emit('delete', client)">
                    <v-list-item-title class="error--text">
                      Delete
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>

        <!-- Client Identifiers Row -->
        <v-row class="caption mt-2">
          <!-- Billing Code -->
          <v-col class="col-auto py-0">
            <span
              :class="`${chipMetaTextClass} chip-label`"
              style="font-weight: 600 !important;"
            >{{ client.billingCode || 'U/K' }}</span>
          </v-col>
        </v-row>
      </v-col>
    </v-card-title>

    <!-- Card Header Divider -->
    <v-divider />

    <!-- Card Content -->
    <v-card-text>
      <!-- Features -->
      <v-col class="pt-2">
        <v-row>
          <!-- Network Code -->
          <v-col class="col-auto py-0">
            <div>
              <font-awesome-icon
                :icon="['fas', 'network-wired']"
                class="midgray--text"
                aria-hidden="true"
              />

              <span
                :class="`ml-2 ${chipMetaTextClass} chip-label`"
              >{{ hasPublisher ? `${clientPublisher.networkCode || 'U/K'}` : 'N/A' }}</span>
            </div>
          </v-col>
        </v-row>

        <!-- Timesheet-Enabled Value -->
        <v-row>
          <v-col class="col-auto py-0">
            <div class="mt-2">
              <font-awesome-icon
                :icon="['fas', 'clock']"
                aria-hidden="true"
                class="midgray--text"
              />

              <span :class="`ml-2 ${chipMetaTextClass} chip-label`">Time {{ client.isTimesheetEnabled ? 'Enabled' : 'Disabled' }}</span>
            </div>
          </v-col>
        </v-row>

        <!-- Administrative Value -->
        <v-row>
          <v-col class="col-auto py-0">
            <div
              v-if="client.isAdministrative === true"
              class="mt-2"
              title="Administrative. Internal Use Only"
            >
              <font-awesome-icon
                v-if="client.isAdministrative"
                :icon="['fas', 'gear']"
                aria-hidden="true"
                class="midgray--text"
              />

              <span :class="`ml-2 ${chipMetaTextClass} chip-label`">
                Admin Use
              </span>
            </div>

            <div
              v-else
              class="mr-2 mt-2"
              title="Standard Client"
            >
              <font-awesome-icon
                :icon="['fas', 'folder']"
                aria-hidden="true"
                class="midgray--text"
              />

              <span :class="`ml-2 ${chipMetaTextClass} chip-label`">
                Standard Use
              </span>
            </div>
          </v-col>
        </v-row>

        <!-- Publisher Values -->
        <v-row>
          <v-col class="col-auto py-0">
            <!-- Teams Count -->
            <div class="mt-2">
              <v-progress-circular
                v-if="loadingPublisher"
                indeterminate
                size="14"
                width="2"
                class="mr-1"
              />

              <font-awesome-icon
                v-else
                :icon="['fas', 'users']"
                aria-hidden="true"
                class="midgray--text"
              />

              <span :class="`ml-2 ${chipMetaTextClass} chip-label`">
                {{ loadingPublisher ? 'Loading...' : `${teamCount} ${teamCount === 1 ? 'Team' : 'Teams'}` }}
              </span>
            </div>
          </v-col>
        </v-row>

        <!-- Reports Count -->
        <v-row>
          <v-col class="col-auto py-0">
            <div class="mt-2">
              <v-progress-circular
                v-if="loadingConfig"
                indeterminate
                size="14"
                width="2"
                class="mr-1"
              />

              <font-awesome-icon
                v-else
                :icon="['fas', 'file-alt']"
                aria-hidden="true"
                class="midgray--text"
              />

              <span :class="`ml-2 ${chipMetaTextClass} chip-label`">
                {{ loadingConfig ? 'Loading...' : `${customReportsCount} ${customReportsCount === 1 ? 'Custom Report' : 'Custom Reports'}` }}
              </span>
            </div>
          </v-col>
        </v-row>
      </v-col>
    </v-card-text>

    <!-- Card Footer Divider -->
    <v-divider />

    <!-- Quick View Button -->
    <v-card-actions>
      <v-col class="col-auto pa-0">
        <v-btn
          text
          small
          color="primary font-weight-medium"
          @click="$emit('view-profile', client)"
        >
          View Details
        </v-btn>
      </v-col>

      <v-spacer />

      <!-- Issues Warning -->
      <v-col
        v-if="hasIssues"
        class="col-auto py-0"
      >
        <v-row class="ma-0">
          <v-col
            v-for="(issue, issueIndex) in allIssues"
            :key="`client-issue-${issueIndex}`"
            class="col-auto pa-0"
          >
            <!-- Issues Chip -->
            <v-chip
              v-if="!fixingConfig"
              small
              outlined
              color="midgray"
              @click="showFixDialog = true"
            >
              <font-awesome-icon
                :icon="['fas', 'exclamation-triangle']"
                class="rounded-pill"
                aria-hidden="true"
              />

              <span
                class="ml-1 chip-label"
              >{{ issue.message }}</span>
            </v-chip>

            <v-chip
              v-else
              small
              outlined
              color="bluegreen"
            >
              <v-progress-circular
                indeterminate
                size="14"
                width="2"
                class="mr-2"
              />

              <span class="ml-1 chip-label">Fixing...</span>
            </v-chip>
          </v-col>
        </v-row>
      </v-col>

      <v-col
        v-else
        class="col-auto py-0 pl-0"
      >
        <v-chip
          v-if="fixingConfig"
          small
          outlined
          color="bluegreen"
        >
          <v-progress-circular
            indeterminate
            size="14"
            width="2"
            class="mr-2"
          />

          <span class="ml-1 chip-label">Fixing...</span>
        </v-chip>

        <v-chip
          v-else
          small
          outlined
          color="bluegreen"
        >
          <font-awesome-icon
            :icon="['fas', 'check-circle']"
            class="rounded-pill"
            aria-hidden="true"
          />

          <span class="ml-1 chip-label">Valid Config</span>
        </v-chip>
      </v-col>
    </v-card-actions>

    <!-- Fix Config Dialog -->
    <fix-config-dialog
      v-model="showFixDialog"
      :client="client"
      :config="config"
      :publisher="publisher"
      @success="handleConfigFixed"
    />
  </v-card>
</template>

<script>
import dates from '@/utils/dates.js'
import themeClasses from '@/mixins/themeClasses'
import FixConfigDialog from './FixConfigDialog.vue'

const isDefinedObject = (val) => val !== null && typeof val === 'object'
const hasObjectKeys = (val) => Object.keys(val).length > 0

export default {
  name: 'ClientCard',
  components: {
    FixConfigDialog,
  },
  mixins: [themeClasses],
  props: {
    client: {
      type: Object,
      required: true,
    },
    config: {
      type: Object,
      default: null,
    },
    publisher: {
      type: Object,
      default: null,
    },
    loadingConfig: {
      type: Boolean,
      default: false,
    },
    loadingPublisher: {
      type: Boolean,
      default: false,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      fixingConfig: false,
      showFixDialog: false,
    }
  },
  computed: {
    chipMetaTextClass() {
      return this.themeTextClass('darkestblue', 'midgray')
    },

    /**
     * @computed hasConfig
     * @type boolean
     * @desc Whether the client has a valid config object
     */
    hasConfig() {
      return isDefinedObject(this.config) && hasObjectKeys(this.config)
    },

    /**
     * @computed clientConfig
     * @type Object
     * @desc Get client config from prop.
     *
     * @note Regarding Client Configs and Organizations
     * The client config is equivalent to the organization document in
     * Firestore, so the terms "client config" and "organization" may be
     * used interchangeably throughout the app's documentation.
     */
    clientConfig() {
      return this.hasConfig ? this.config : {}
    },

    /**
     * @computed access
     * @type Object
     * @desc Access object from client config
     */
    accessConfig() {
      return this.hasConfig ? this.clientConfig.access : {}
    },

    /**
     * @computed hasAccess
     * @type boolean
     * @desc Whether the client config has a valid, non-empty access object
     */
    hasAccessConfig() {
      return isDefinedObject(this.accessConfig) && hasObjectKeys(this.accessConfig)
    },

    /**
     * @computed hasPermissions
     * @type boolean
     * @desc Whether the client config has a valid, non-empty permissions object
     */
    hasPermissions() {
      const { permissions } = this.accessConfig
      return this.hasAccessConfig && isDefinedObject(permissions) && hasObjectKeys(permissions)
    },

    /**
     * @computed permissions
     * @type Object
     * @desc The client config's permissions object
     */
    permissions() {
      return this.hasPermissions ? this.accessConfig.permissions : {}
    },

    /**
     * @computed hasSettings
     * @type boolean
     * @desc Whether the client config has a valid settings object, regardless
     * of its keys.
     */
    hasSettings() {
      const { settings } = this.clientConfig
      return isDefinedObject(settings)
    },

    /**
     * @computed settings
     * @type Object
     * @desc The client config's settings object
     */
    settings() {
      return this.hasSettings ? this.clientConfig.settings : {}
    },

    /**
     * @computed hasPublisher
     * @type boolean
     * @desc Whether the client has a publisher
     */
    hasPublisher() {
      return isDefinedObject(this.publisher)
    },

    /**
     * @computed clientPublisher
     * @type Object
     * @desc Get client publisher from prop
     */
    clientPublisher() {
      return this.hasPublisher ? this.publisher : {}
    },

    /**
     * @computed accessExpires
     * @type string
     * @desc Determine if the organization's access has expired based on the
     * access.expires date on the client config.
     *
     * @note If access.expires is not defined, return what the API considers
     * the max date to avoid false positives on expiry.
     */
    accessExpires() {
      const maxDateFromApi = '2200-01-01T00:00:00'
      return this.accessConfig.expires || maxDateFromApi
    },

    /**
     * @computed isExpiredClient
     * @type boolean
     * @desc Determine if the organization's access has expired based on the
     * access.expires date on the client config.
     */
    isExpiredClient() {
      return dates.areDatesGte(this.accessExpires, dates.now())
    },

    /**
     * @computed clientStatus
     * @type boolean
     * @desc Determine client status based on isActive. A client is active
     * when it is available for administrative and operational tasks. The
     * client's status affects the publisher and users under the client
     * by extension. For example, if the client is inactive, all associated
     * publishers and users are considered inactive regardless of their
     * individual status.
     */
    clientStatus() {
      return this.client.isActive || false
    },

    /**
     * @computed publisherStatus
     * @type boolean
     * @desc Determine publisher status based on isActive. A publisher may
     * be inactive while the client is active to accommodate situations like
     * billing, account suspension, and other administrative actions.
     * However, if the client is inactive, the publisher and users are
     * considered inactive by extension.
     */
    publisherStatus() {
      return this.publisher?.isActive || false
    },

    /**
     * @computed orgStatus
     * @type boolean
     * @desc Determine organization status based on the organization
     * document's access.active property. When false, all users associated
     * with the organization are considered inactive, regardless of the
     * user, client or publisher's status.
     */
    orgStatus() {
      return this.accessConfig.active || false
    },

    /**
     * Client status color using color psychology:
     * - Blue (primary): Active/engaged
     * - Gray (midgray): Inactive/neutral
     * - Red (reds): Config errors/critical
     */
    clientStatusColor() {
      const { isActive } = this.client
      // The accessConfig getter ensures accessConfig is always a valid object.
      const configActive = this.accessConfig.active !== false

      // Both active - show trust/stability color
      if (isActive && configActive) return 'retrogreendark'

      // Client inactive - show neutral color
      if (!isActive) return 'midgray'

      // Config has issues - show alert color
      if (!configActive) return 'reds'

      return 'midgray'
    },

    /**
     * Client status icon
     */
    clientStatusIcon() {
      const { isActive } = this.client
      const configActive = this.clientConfig?.access?.active !== false

      if (isActive && configActive) return ['fas', 'check-circle']
      if (!isActive) return ['fas', 'ban']
      if (!configActive) return ['fas', 'exclamation-circle']

      return ['fas', 'question-circle']
    },

    /**
     * Client status text
     */
    clientStatusText() {
      const { isActive, isDeleted } = this.client

      const isConfigActive = this.clientConfig?.access?.active !== false

      if (isDeleted === true) {
        return 'Deleted'
      }

      if (!isActive) {
        return 'Inactive'
      }

      if (isActive && isConfigActive) {
        return 'Active'
      }

      return 'Unknown'
    },

    /**
     * Config active status icon
     */
    configActiveIcon() {
      const isActive = this.clientConfig?.access?.active !== false
      return isActive ? ['fas', 'check-circle'] : ['fas', 'times-circle']
    },

    /**
     * Config active status class
     */
    configActiveClass() {
      const isActive = this.clientConfig?.access?.active !== false
      return isActive ? 'success--text' : 'error--text'
    },

    /**
     * Config active status text
     */
    configActiveText() {
      const isActive = this.clientConfig?.access?.active !== false
      return isActive ? 'Active' : 'Inactive'
    },

    /**
     * @computed hasPermissionToPacing
     * @desc Whether the client has Pacing permissions
     */
    hasPermissionToPacing() {
      return this.permissions.pacing === true
    },

    /**
     * @computed hasPermissionToDiscrepancy
     * @desc Whether the client has Discrepancy permissions
     */
    hasPermissionToDiscrepancy() {
      return this.permissions.discrepancy === true
    },

    /**
     * @computed customReports
     * @desc Get Custom Reports permissions array
     */
    customReports() {
      // The permissions getter ensures permissions is always a valid object.
      const { customReports } = this.permissions

      return (customReports && Array.isArray(customReports)) ? customReports : []
    },

    /**
     * @computed customReportsCount
     * @desc Get the number of Custom Reports configured for the client.
     */
    customReportsCount() {
      const { customReports } = this

      // The customReports getter ensures customReports is always an array.
      return customReports.length
    },

    /**
     * @computed hasPermissionToCustomReports
     * @desc Whether the client has Custom Reports permissions
     */
    hasPermissionToCustomReports() {
      const { customReports } = this
      return customReports.length > 0
    },

    /**
     * Team count from publisher
     */
    teamCount() {
      if (!this.clientPublisher) return 0

      const gamTeams = this.clientPublisher.teams?.length || 0
      const productTeams = this.clientConfig?.settings?.teams?.length || 0

      const totalTeamsInSearch = gamTeams + productTeams

      return totalTeamsInSearch === 0 ? 'All' : totalTeamsInSearch
    },

    /**
     * Check if client has issues
     */
    allIssues() {
      const issues = []

      const {
        hasConfig,
        hasAccessConfig,
        hasPermissions,
        hasSettings,
      } = this

      if (!hasConfig) {
        issues.push({
          code: 'missing-config',
          message: 'Fix Config',
        })
      }

      if (hasConfig && !hasAccessConfig) {
        issues.push({
          code: 'invalid-access',
          message: 'Fix Access',
        })
      }

      if (hasConfig && !hasPermissions) {
        issues.push({
          code: 'invalid-permissions',
          message: 'Fix Perms',
        })
      }

      if (hasConfig && !hasSettings) {
        issues.push({
          code: 'invalid-settings',
          message: 'Fix Settings',
        })
      }

      return issues
    },

    hasIssues() {
      const issues = this.allIssues

      return issues.length > 0
    },

    allReports() {
      const map = {
        pacing: (val) => val.hasPacing,
        discrepancy: (val) => val.hasDiscrepancy,
        adxHealth: (val) => val.hasAdxHealth,
        networkHealth: (val) => val.hasNetworkHealth,
        customReports: (val) => val.customReports && val.customReports.length > 0,
        yieldPartner: (val) => val.hasYieldPartner,
      }

      if (!this.clientPublisher) {
        return []
      }

      return Object.keys(map).reduce((acc, key) => {
        const hasReport = map[key](this.clientPublisher || {})
        if (hasReport) acc.push(key)
        return acc
      }, [])
    },
  },
  methods: {
    /**
     * Handle config fixed event from dialog
     */
    handleConfigFixed(billingCode) {
      this.fixingConfig = false
      this.$emit('config-fixed', billingCode)
    },
  },
}
</script>

<style lang="scss" scoped>
.chip-label {
  font-weight: 500 !important;
  letter-spacing: 0.1px;
}

.client-card {
  transition: box-shadow 0.2s ease, transform 0.1s ease;
  border: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
    transform: translateY(-1px);
  }

  &.has-issues {
    border-left: 3px solid var(--v-error-base);
  }

  .clickable {
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.7;
    }
  }

  .col-grow {
    flex-grow: 1;
    min-width: 0;
  }

  .v-card__title {
    .v-row {
      margin-left: -8px;
      margin-right: -8px;
    }
  }

  .v-card__text {
    flex-grow: 1;

    .subtitle-2 {
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      font-size: 0.7rem;
    }
  }
}

// Dark mode support
.theme--dark {
  .client-card {
    border-color: rgba(255, 255, 255, 0.12);

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3) !important;
    }

    &.has-issues {
      border-left-color: var(--v-error-lighten1);
    }
  }
}
</style>
